import { FC, useEffect, useState, ReactNode } from 'react';
import {
  RiTaskLine,
  RiFontSize,
  RiBold,
  RiNumber6,
  RiBookmark3Line,
} from '@remixicon/react';
import Tab, { TabVariant } from '../../atoms/Tab';
import Tabs, { TabsVariant } from '../../molecules/Tabs';
import QualityCheckRow, { STATUS } from '../../molecules/QualityCheckRow';
import { translate } from '../../../localization';
import styles from './QcReview.module.css';
import ButtonSkeleton from '../../atoms/ButtonSkeleton';
import Toggle from '../../atoms/Toggle/Toggle';
import { QcReviewName, QualityCheckType } from '../../../types/QualityChecks';
import IsiPiIcon from '../../atoms/IsiPiIcon';
import { ApplicableRecommendationData } from '../../../types/contentValidation';
import { segregateRecommendations } from '../../../utils/segregateRecommendations';
import Tag, { TagVariant } from '../../atoms/Tag';

enum BrandPageTabName {
  NEEDS_REVIEW = 'Needs Review',
  PASSED = 'Passed',
  NOT_APPLICABLE = 'Not Applicable',
}

enum QcTypeName {
  SPELLING_AND_GRAMMAR = 'SPELLING_AND_GRAMMAR',
  VERBAL_IDENTITY_GUIDELINES = 'VERBAL_IDENTITY_GUIDELINES',
  CLAIMS_AND_REFERENCES = 'CLAIMS_AND_REFERENCES',
  NEW_BRAND_RULE = 'NEW_BRAND_RULE',
  ISI_OR_PI_APPLICABILITY = 'ISI_OR_PI_APPLICABILITY',
  CHANNEL_BEST_PRACTICES = 'CHANNEL_BEST_PRACTICES',
}

interface TabList {
  tabName: BrandPageTabName;
  typeCount: number;
}
type QcDataType = {
  [QcTypeName: string]: {
    icon?: ReactNode;
    title?: string;
    toolTipText?: string;
    description?: string;
  };
};
const QcData: QcDataType = {
  [QcTypeName.CHANNEL_BEST_PRACTICES]: {
    icon: <RiBookmark3Line size={18} color='#0000C9' />,
    title: '',
    toolTipText: '',
    description: '',
  },
  [QcTypeName.CLAIMS_AND_REFERENCES]: {
    icon: <RiTaskLine size={18} color='#0000C9' />,
    title: translate('claims_and_references'),
    toolTipText: translate('claims_and_references_validation_info'),
    description: translate('claims_and_references_description'),
  },
  [QcTypeName.ISI_OR_PI_APPLICABILITY]: {
    icon: <IsiPiIcon />,
    title: translate('isi_and_pi'),
    toolTipText: translate('isi_and_pi_validation_info'),
    description: translate('isi_and_pi_description'),
  },
  [QcTypeName.SPELLING_AND_GRAMMAR]: {
    icon: <RiFontSize size={18} color='#0000C9' />,
    title: translate('spelling_grammar'),
    toolTipText: translate('spelling_grammar_validation_info'),
    description: translate('spelling_grammar_description'),
  },
  [QcTypeName.VERBAL_IDENTITY_GUIDELINES]: {
    icon: <RiBold size={18} color='#0000C9' />,
    title: translate('pfizer_brand_verbal_identity'),
    toolTipText: translate('pfizer_brand_verbal_identity_validation_info'),
  },
  [QcTypeName.NEW_BRAND_RULE]: {
    icon: <RiNumber6 size={18} color='#0000C9' />,
    title: translate('six_month_rule'),
    toolTipText: translate('six_month_rule_validation_info'),
  },
};

function countTypes(qualityCheckData: QualityCheckType): {
  [reviewType: string]: number;
} {
  const typeCounts: { [reviewType: string]: number } = {};

  // Iterate over each reviewType in the qualityCheckData using Object.entries
  Object.entries(qualityCheckData).forEach(([reviewType, categories]) => {
    let count = 0;

    // Iterate over each category in the current reviewType
    Object.values(categories).forEach(typeArray => {
      count += typeArray.length;
    });

    typeCounts[reviewType] = count;
  });

  return typeCounts;
}

type QcRecommendationProps = {
  recommendationId: string;
  recommendationText: string;
  isSelected: boolean;
  onChange: (selectedRecommendationId: string) => void;
};

const QcRecommendation: FC<QcRecommendationProps> = ({
  recommendationId,
  recommendationText,
  isSelected,
  onChange,
}) => {
  const handleToggleOnChange = () => {
    onChange(recommendationId);
  };

  return (
    <div className={styles['qc-recommendation-section-category__list__item']}>
      <p
        className={
          styles['qc-recommendation-section-category__list__item__text']
        }
      >
        {recommendationText}
      </p>

      <Toggle
        checked={isSelected}
        onChange={handleToggleOnChange}
        isDisabled={false}
        label=''
        tooltipText=''
        uniqueKey={recommendationId}
      />
    </div>
  );
};

type QcReviewProps = {
  qualityChecksData: QualityCheckType;
  recommendationsData: ApplicableRecommendationData[];
  isRecommendationVisible: boolean;
  onContentBoost: () => void;
  onRecommendationToggle: (selectedRecommendationId: string) => void;
};
const QcReview: FC<QcReviewProps> = ({
  qualityChecksData,
  recommendationsData = [],
  isRecommendationVisible = false,
  onContentBoost,
  onRecommendationToggle,
}) => {
  const [tabs, setTabs] = useState<TabList[]>([]);
  const segregatedRecommendationData =
    segregateRecommendations(recommendationsData);

  const [activeTab, setActiveTab] = useState(BrandPageTabName.NEEDS_REVIEW);

  const boostContentDisabled =
    recommendationsData.length === 0 ||
    !recommendationsData.some(recommendation => recommendation.isSelected);

  const handleTabClick = (selectedTab: BrandPageTabName) => {
    setActiveTab(selectedTab);
  };

  useEffect(() => {
    const tabList: TabList[] = [];
    const reviewCountType = countTypes(qualityChecksData);

    // Check for the existence of each category type and add the corresponding tab
    if (
      Object.prototype.hasOwnProperty.call(
        qualityChecksData,
        QcReviewName.NEEDS_REVIEW
      )
    ) {
      tabList.push({
        tabName: BrandPageTabName.NEEDS_REVIEW,
        typeCount: reviewCountType[QcReviewName.NEEDS_REVIEW],
      });
    }
    if (
      Object.prototype.hasOwnProperty.call(
        qualityChecksData,
        QcReviewName.PASSED
      )
    ) {
      tabList.push({
        tabName: BrandPageTabName.PASSED,
        typeCount: reviewCountType[QcReviewName.PASSED],
      });
    }
    if (
      Object.prototype.hasOwnProperty.call(
        qualityChecksData,
        QcReviewName.NOT_APPLICABLE
      )
    ) {
      tabList.push({
        tabName: BrandPageTabName.NOT_APPLICABLE,
        typeCount: reviewCountType[QcReviewName.NOT_APPLICABLE],
      });
    }

    setTabs(tabList);
  }, []);

  const handleContentBoostClick = () => {
    onContentBoost();
  };

  const handleRecomendationToggleChange = (
    selectedRecommendationId: string
  ) => {
    onRecommendationToggle(selectedRecommendationId);
  };

  useEffect(() => {
    if (qualityChecksData[QcReviewName.NEEDS_REVIEW]) {
      setActiveTab(BrandPageTabName.NEEDS_REVIEW);
    } else if (qualityChecksData[QcReviewName.PASSED]) {
      setActiveTab(BrandPageTabName.PASSED);
    } else {
      setActiveTab(BrandPageTabName.NOT_APPLICABLE);
    }
  }, []);
  return (
    <div className={styles['qc-review']}>
      <div className={`${styles['qc-review-section']}`}>
        <Tabs
          variant={TabsVariant.SPACED}
          customStyle={styles['qc-review-section-tabs']}
        >
          {tabs.map(tab => {
            return (
              <Tab
                variant={TabVariant.SPACED}
                key={tab.tabName}
                onClick={() => handleTabClick(tab.tabName)}
                isActive={activeTab === tab.tabName}
                customStyle={
                  activeTab === tab.tabName
                    ? styles['review-tab--active']
                    : styles['review-tab']
                }
              >
                {tab.tabName} {tab.typeCount && `(${tab.typeCount})`}
              </Tab>
            );
          })}
        </Tabs>
        <div className={styles.tab_details}>
          {qualityChecksData[QcReviewName.NEEDS_REVIEW] &&
            activeTab === BrandPageTabName.NEEDS_REVIEW && (
              <>
                {Object.keys(qualityChecksData[QcReviewName.NEEDS_REVIEW]).map(
                  el => {
                    return (
                      <div key={el} className={styles['category-wrapper']}>
                        <h6 className={styles['category-wrapper__title']}>
                          {el}
                        </h6>
                        {qualityChecksData[QcReviewName.NEEDS_REVIEW][el].map(
                          categoryType => {
                            return (
                              <QualityCheckRow
                                key={categoryType.type}
                                icon={QcData[categoryType.type].icon}
                                title={categoryType.name}
                                description={categoryType.summary}
                                toolTipText={
                                  QcData[categoryType.type].toolTipText
                                }
                                status={STATUS.REVIEW_SUGGESTED}
                                isApplicable
                              />
                            );
                          }
                        )}
                      </div>
                    );
                  }
                )}
              </>
            )}

          {qualityChecksData[QcReviewName.PASSED] &&
            activeTab === BrandPageTabName.PASSED && (
              <>
                {Object.keys(qualityChecksData[QcReviewName.PASSED]).map(el => {
                  return (
                    <div key={el} className={styles['category-wrapper']}>
                      <h6 className={styles['category-wrapper__title']}>
                        {el}
                      </h6>
                      {qualityChecksData[QcReviewName.PASSED][el].map(
                        categoryType => {
                          return (
                            <QualityCheckRow
                              key={categoryType.type}
                              icon={QcData[categoryType.type].icon}
                              title={categoryType.name}
                              toolTipText={
                                QcData[categoryType.type].toolTipText
                              }
                              status={STATUS.VALIDATED}
                              isApplicable
                            />
                          );
                        }
                      )}
                    </div>
                  );
                })}
              </>
            )}

          {qualityChecksData[QcReviewName.NOT_APPLICABLE] &&
            activeTab === BrandPageTabName.NOT_APPLICABLE && (
              <>
                {Object.keys(
                  qualityChecksData[QcReviewName.NOT_APPLICABLE]
                ).map(el => {
                  return (
                    <div key={el} className={styles['category-wrapper']}>
                      <h6 className={styles['category-wrapper__title']}>
                        {el}
                      </h6>
                      {qualityChecksData[QcReviewName.NOT_APPLICABLE][el].map(
                        categoryType => {
                          return (
                            <QualityCheckRow
                              key={categoryType.type}
                              icon={QcData[categoryType.type].icon}
                              title={categoryType.name}
                              description={categoryType.summary}
                              toolTipText={
                                QcData[categoryType.type].toolTipText
                              }
                              status={STATUS.REVIEW_SUGGESTED}
                              isApplicable={false}
                            />
                          );
                        }
                      )}
                    </div>
                  );
                })}
              </>
            )}
        </div>
      </div>
      {isRecommendationVisible && (
        <div className={`${styles['qc-recommendation-section']}`}>
          <div className={styles['qc-recommendation-section-header']}>
            <h3 className={styles['qc-recommendation-section-header__title']}>
              {translate('recommendations')} {`(${recommendationsData.length})`}
            </h3>

            <div className={styles['qc-recommendation-section__boost-group']}>
              <ButtonSkeleton
                id='boost-content'
                aria-label={translate('boost_content')}
                className={styles['boost-content__btn']}
                type='button'
                onClick={handleContentBoostClick}
                disabled={boostContentDisabled}
              >
                <span
                  className={`${
                    boostContentDisabled
                      ? styles['boost-content__btn__text--disabled']
                      : ''
                  } ${styles['boost-content__btn__text']}`}
                >
                  {translate('boost_content')}
                </span>
              </ButtonSkeleton>
              <Tag
                text={translate('beta')}
                variant={TagVariant.NEUTRAL}
                icon={null}
                classes={styles['qc-review__beta-tag']}
              />
            </div>
          </div>
          {Object.entries(segregatedRecommendationData).map(
            ([category, recommendations]) => (
              <div
                key={category}
                className={styles['qc-recommendation-section-category']}
              >
                <h6
                  className={
                    styles['qc-recommendation-section-category__title']
                  }
                >
                  {category}
                </h6>
                <div
                  className={styles['qc-recommendation-section-category__list']}
                >
                  {recommendations.map(recommendation => {
                    const { recommendationId, isSelected, recommendationText } =
                      recommendation;
                    return (
                      <QcRecommendation
                        key={recommendationId}
                        recommendationId={recommendationId}
                        recommendationText={recommendationText}
                        isSelected={isSelected}
                        onChange={handleRecomendationToggleChange}
                      />
                    );
                  })}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

QcReview.displayName = 'QcReview';

export default QcReview;
